// ════════════════════════════════════════════════════════════════════════════
// CMS & HOMEPAGE CONFIG CONTRACTS
// Shared types, defaults, constants, and resolvers for storefront and admin panels.
// ════════════════════════════════════════════════════════════════════════════

// ── Festival Themes Types & Constants ───────────────────────────────────────

export type FestivalPresetId =
  | 'none'
  | 'diwali'
  | 'holi'
  | 'eid'
  | 'christmas'
  | 'newyear'
  | 'independence'
  | 'custom';

export interface FestivalPalette {
  primary: string;
  hover: string;
  gold: string;
  goldHover?: string | undefined;
  topbarBg: string;
  topbarText: string;
}

export interface FestivalAnnouncement {
  text: string;
  bg: string;
  fg: string;
}

export interface FestivalPreset {
  id: FestivalPresetId;
  name: string;
  emoji: string;
  description: string;
  palette: FestivalPalette;
  announcement: FestivalAnnouncement | null;
}

export interface FestivalSchedule {
  startsAt?: string | null;
  endsAt?: string | null;
}

/** The currently-applied festival theme as stored under `homepage_festival_theme`. */
export interface FestivalThemeSetting {
  active: boolean;
  presetId: FestivalPresetId;
  /** Used only when `presetId === 'custom'`. */
  customPalette?: FestivalPalette | undefined;
  customAnnouncement?: FestivalAnnouncement | null | undefined;
  /** Optional auto-activation window (e.g. schedule a Diwali theme Oct 25 → Nov 5). */
  schedule?: FestivalSchedule | null | undefined;
}

export const FESTIVAL_THEMES: Record<Exclude<FestivalPresetId, 'custom'>, FestivalPreset> = {
  none: {
    id: 'none',
    name: 'No Festival Theme',
    emoji: '🚫',
    description: 'Disable any festival overlay. Storefront falls back to its default palette.',
    palette: {
      primary: '#d63232',
      hover: '#b92d2d',
      gold: '#d9a93a',
      goldHover: '#b98b2c',
      topbarBg: '#2b2b2b',
      topbarText: '#ffffff',
    },
    announcement: null,
  },
  diwali: {
    id: 'diwali',
    name: 'Diwali Dhamaka',
    emoji: '🪔',
    description: 'Warm reds + bright gold. Deep maroon top bar.',
    palette: {
      primary: '#dc2626',
      hover: '#991b1b',
      gold: '#ffd700',
      goldHover: '#d4a017',
      topbarBg: '#4a0e0e',
      topbarText: '#ffd700',
    },
    announcement: {
      text: '✨ Diwali Dhamaka — Up to 70% off on Sweets, Spices & Pooja Essentials',
      bg: 'linear-gradient(90deg, #4a0e0e 0%, #7c1d1d 50%, #4a0e0e 100%)',
      fg: '#ffd700',
    },
  },
  holi: {
    id: 'holi',
    name: 'Happy Holi',
    emoji: '🎨',
    description: 'Vibrant pink + yellow + purple top bar.',
    palette: {
      primary: '#ec4899',
      hover: '#be185d',
      gold: '#fbbf24',
      goldHover: '#d97706',
      topbarBg: '#581c87',
      topbarText: '#fef3c7',
    },
    announcement: {
      text: '🎨 Happy Holi — Splash of Savings on Colors, Sweets & Treats',
      bg: 'linear-gradient(90deg, #ec4899 0%, #f59e0b 50%, #8b5cf6 100%)',
      fg: '#ffffff',
    },
  },
  eid: {
    id: 'eid',
    name: 'Eid Mubarak',
    emoji: '🌙',
    description: 'Emerald + amber. Dark green top bar.',
    palette: {
      primary: '#059669',
      hover: '#047857',
      gold: '#f59e0b',
      goldHover: '#d97706',
      topbarBg: '#064e3b',
      topbarText: '#fde68a',
    },
    announcement: {
      text: '🌙 Eid Mubarak — Festive Deals on Dates, Sweets & Premium Gifts',
      bg: 'linear-gradient(90deg, #064e3b 0%, #047857 50%, #064e3b 100%)',
      fg: '#fde68a',
    },
  },
  christmas: {
    id: 'christmas',
    name: 'Christmas Cheer',
    emoji: '🎄',
    description: 'Classic christmas red + pine green.',
    palette: {
      primary: '#dc2626',
      hover: '#991b1b',
      gold: '#16a34a',
      goldHover: '#15803d',
      topbarBg: '#14532d',
      topbarText: '#fef2f2',
    },
    announcement: {
      text: '🎄 Christmas Cheer — Gift Sale on Premium Hampers & Goodies',
      bg: 'linear-gradient(90deg, #14532d 0%, #166534 50%, #14532d 100%)',
      fg: '#fef2f2',
    },
  },
  newyear: {
    id: 'newyear',
    name: 'New Year Bash',
    emoji: '🎆',
    description: 'Indigo + gold. Midnight blue top bar.',
    palette: {
      primary: '#6366f1',
      hover: '#4338ca',
      gold: '#facc15',
      goldHover: '#eab308',
      topbarBg: '#1e1b4b',
      topbarText: '#facc15',
    },
    announcement: {
      text: '🎆 New Year Bash — Fresh Starts, Fresh Deals — Up to 60% off',
      bg: 'linear-gradient(90deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)',
      fg: '#facc15',
    },
  },
  independence: {
    id: 'independence',
    name: 'Independence Day',
    emoji: '🇮🇳',
    description: 'Tricolor — saffron red, white, India green.',
    palette: {
      primary: '#dc2626',
      hover: '#991b1b',
      gold: '#ffffff',
      goldHover: '#e5e7eb',
      topbarBg: '#15803d',
      topbarText: '#ffffff',
    },
    announcement: {
      text: '🇮🇳 Independence Day — Made in India Mega Sale — Vocal for Local',
      bg: 'linear-gradient(90deg, #ea580c 0%, #ffffff 50%, #15803d 100%)',
      fg: '#1f2937',
    },
  },
};

export const PRESET_ORDER: FestivalPresetId[] = [
  'none',
  'diwali',
  'holi',
  'eid',
  'christmas',
  'newyear',
  'independence',
  'custom',
];

export const DEFAULT_FESTIVAL_THEME: FestivalThemeSetting = {
  active: false,
  presetId: 'none',
};

/** Resolve which palette + announcement should actually render. */
export function resolveFestivalTheme(
  setting: FestivalThemeSetting | null | undefined,
): { palette: FestivalPalette; announcement: FestivalAnnouncement | null } | null {
  if (!setting || !setting.active || setting.presetId === 'none') return null;
  if (setting.presetId === 'custom') {
    return {
      palette: setting.customPalette ?? FESTIVAL_THEMES.none.palette,
      announcement: setting.customAnnouncement ?? null,
    };
  }
  const preset = FESTIVAL_THEMES[setting.presetId];
  if (!preset) return null;
  return { palette: preset.palette, announcement: preset.announcement };
}

/** Used by both storefront (live apply) and admin (preview swatches). */
export const CSS_VAR_MAP: Record<keyof FestivalPalette, string> = {
  primary: '--color-primary-red',
  hover: '--color-primary-hover',
  gold: '--color-primary-gold',
  goldHover: '--color-primary-gold-hover',
  topbarBg: '--color-topbar-bg',
  topbarText: '--color-topbar-text',
};

// ── Homepage Sections Types & Catalog ───────────────────────────────────────

export type HomepageSectionId =
  | 'hero'
  | 'energy_strip'
  | 'promo_cards'
  | 'shop_by_category'
  | 'flash_deal_countdown'
  | 'flash_deals_banners'
  | 'todays_deal_banners'
  | 'banner_level_1'
  | 'highlights_strip'
  | 'bottom_grid_2'
  | 'banner_level_2'
  | 'best_selling'
  | 'top_sellers'
  | 'bharat_trust'
  | 'banner_level_3'
  | 'category_wise_products'
  | 'classifieds'
  | 'top_brands';

export interface HomepageSectionMeta {
  id: HomepageSectionId;
  label: string;
  description: string;
}

/** Default order = declaration order. Admin re-orders by saving a new sequence. */
export const HOMEPAGE_SECTION_CATALOG: HomepageSectionMeta[] = [
  {
    id: 'hero',
    label: 'Hero Slider + Sidebar',
    description: 'Full-width hero with category sidebar (curated artisan links).',
  },
  {
    id: 'energy_strip',
    label: 'Announcement Strip',
    description: 'Glowing premium announcement strip beneath the hero.',
  },
  {
    id: 'promo_cards',
    label: 'Promo Cards Grid',
    description: 'Horizontally scrollable theme-coloured category promo cards.',
  },
  {
    id: 'shop_by_category',
    label: 'Shop by Category + Popular Picks',
    description: 'Sidebar accordion of all categories + admin-featured popular picks carousel.',
  },
  {
    id: 'flash_deal_countdown',
    label: 'Flash Sale Countdown',
    description:
      'Live countdown banner for the currently active flash deal (auto-hidden when none).',
  },
  {
    id: 'flash_deals_banners',
    label: 'Flash Deals Banners',
    description: 'Banner grid driven by FLASH_DEALS placement.',
  },
  {
    id: 'todays_deal_banners',
    label: "Today's Deal Banners",
    description: 'Banner grid driven by TODAYS_DEAL placement.',
  },
  {
    id: 'banner_level_1',
    label: 'Banner Level 1',
    description: 'Banner grid driven by BANNER_LEVEL_1 placement.',
  },
  {
    id: 'highlights_strip',
    label: 'Highlights Strip / Energy Cards',
    description:
      'Three highlight cards — CMS banners when configured, otherwise the energy_cards setting.',
  },
  {
    id: 'bottom_grid_2',
    label: 'Bottom Grid 2 (Curated)',
    description:
      'Admin-curated product grid for `homepageSection=BOTTOM_GRID_2` (auto-hidden when empty).',
  },
  {
    id: 'banner_level_2',
    label: 'Banner Level 2',
    description: 'Banner grid driven by BANNER_LEVEL_2 placement.',
  },
  {
    id: 'best_selling',
    label: 'Best Selling Products',
    description: 'Admin-curated best-selling products grid.',
  },
  {
    id: 'top_sellers',
    label: 'Top Sellers Vendor Showcase',
    description: 'Verified vendor showcase (top_sellers setting).',
  },
  {
    id: 'bharat_trust',
    label: 'Bharat Trust Section',
    description: 'Trust badges + made-in-India messaging block.',
  },
  {
    id: 'banner_level_3',
    label: 'Banner Level 3',
    description: 'Banner grid driven by BANNER_LEVEL_3 placement.',
  },
  {
    id: 'category_wise_products',
    label: 'Category Wise Products',
    description: 'Banner grid driven by CATEGORY_PRODUCTS placement.',
  },
  {
    id: 'classifieds',
    label: 'Classifieds',
    description:
      'Banner grid driven by CLASSIFIEDS placement (gated by `classifiedProduct` feature flag).',
  },
  {
    id: 'top_brands',
    label: 'Top Brands',
    description: 'Banner grid driven by TOP_BRANDS placement.',
  },
];

export interface HomepageLayoutItem {
  id: HomepageSectionId;
  visible: boolean;
}

export interface HomepageLayoutSetting {
  items: HomepageLayoutItem[];
}

/** Default layout = catalog order, every section visible. */
export const DEFAULT_HOMEPAGE_LAYOUT: HomepageLayoutSetting = {
  items: HOMEPAGE_SECTION_CATALOG.map((s) => ({ id: s.id, visible: true })),
};

/**
 * Reconcile an admin-saved layout against the catalog:
 * - Drop items whose ids no longer exist (catalog was updated)
 * - Append any catalog ids missing from the saved layout (new sections added)
 *   to the end, visible by default
 * - Preserve admin-set order + visibility for everything else
 */
export function resolveHomepageLayout(
  saved: HomepageLayoutSetting | null | undefined,
): HomepageLayoutItem[] {
  const catalogIds = new Set(HOMEPAGE_SECTION_CATALOG.map((s) => s.id));

  if (!saved || !Array.isArray(saved.items) || saved.items.length === 0) {
    return DEFAULT_HOMEPAGE_LAYOUT.items.slice();
  }

  // Keep only known ids, in admin's saved order
  const seen = new Set<HomepageSectionId>();
  const kept: HomepageLayoutItem[] = [];
  for (const item of saved.items) {
    if (catalogIds.has(item.id) && !seen.has(item.id)) {
      seen.add(item.id);
      kept.push({ id: item.id, visible: item.visible !== false });
    }
  }

  // Append any catalog ids the admin's layout doesn't mention (new sections)
  for (const meta of HOMEPAGE_SECTION_CATALOG) {
    if (!seen.has(meta.id)) {
      kept.push({ id: meta.id, visible: true });
    }
  }

  return kept;
}

// ── Homepage Block Elements & Defaults ──────────────────────────────────────

export interface PromoCardData {
  id: string;
  href: string;
  themeClass: string;
  imageUrl: string;
  imageAlt: string;
  objectPosition?: string | undefined;
  badge: string;
  title: string;
  description: string;
  ctaText: string;
}

export interface EnergyCardData {
  id: string;
  title: string;
  body: string;
  icon?: string | undefined;
  badge?: string | undefined;
}

export interface TopSellerData {
  id: string;
  name: string;
  tag: string;
  color: string;
  rating: number;
  reviews: number;
  href?: string | undefined;
}

export interface SectionHeadersData {
  popularPicks?: {
    badge?: string | undefined;
    title?: string | undefined;
    subtitle?: string | undefined;
  };
  bottomGrid2?: {
    badge?: string | undefined;
    title?: string | undefined;
    subtitle?: string | undefined;
  };
  bestSelling?: {
    badge?: string | undefined;
    title?: string | undefined;
    subtitle?: string | undefined;
    viewAllLabel?: string | undefined;
  };
  topSellers?: {
    title?: string | undefined;
    subtitle?: string | undefined;
    viewAllLabel?: string | undefined;
  };
  flashSale?: {
    badge?: string | undefined;
    heading?: string | undefined;
    subheading?: string | undefined;
  };
}

export const THEME_OPTIONS = [
  'theme-spice',
  'theme-flour',
  'theme-pickle',
  'theme-sweets',
  'theme-oil',
  'theme-ghee',
  'theme-dryfruits',
];

export const DEFAULT_PROMO_CARDS: PromoCardData[] = [
  {
    id: 'spice',
    href: '/search?q=spices',
    themeClass: 'theme-spice',
    imageUrl:
      'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Spices',
    objectPosition: 'center',
    badge: 'Premium Masala',
    title: 'Pure & Aromatic',
    description: 'Hand-ground • 100% Organic',
    ctaText: 'Explore Range',
  },
  {
    id: 'flour',
    href: '/search?q=flour',
    themeClass: 'theme-flour',
    imageUrl:
      'https://images.unsplash.com/photo-1574316071802-0d684efa7bf5?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Flours',
    objectPosition: 'center 40%',
    badge: 'Premium Grains',
    title: 'Sharbati Atta',
    description: 'Stone-Ground • High Fiber',
    ctaText: 'Shop Now',
  },
  {
    id: 'pickle',
    href: '/search?q=pickle',
    themeClass: 'theme-pickle',
    imageUrl:
      'https://images.unsplash.com/photo-1601004890684-d8cbf643f5f2?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Pickles',
    objectPosition: 'center',
    badge: "Dadi's Recipe",
    title: 'Spicy Mango',
    description: 'Sun-dried • Traditional',
    ctaText: 'Buy Now',
  },
  {
    id: 'sweets',
    href: '/search?q=sweets',
    themeClass: 'theme-sweets',
    imageUrl:
      'https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Sweets',
    objectPosition: 'center',
    badge: 'Pure Ghee Mithai',
    title: 'Handmade Delights',
    description: 'Freshly Made • Gift Box',
    ctaText: 'Explore Sweets',
  },
  {
    id: 'oil',
    href: '/search?q=oil',
    themeClass: 'theme-oil',
    imageUrl:
      'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Oils',
    objectPosition: 'center',
    badge: 'Cold-Pressed',
    title: 'Kacchi Ghani',
    description: 'Pure Mustard • Chemical Free',
    ctaText: 'Explore Oils',
  },
  {
    id: 'ghee',
    href: '/search?q=ghee',
    themeClass: 'theme-ghee',
    imageUrl:
      'https://images.unsplash.com/photo-1589733901241-5e53429e1db4?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Ghee',
    objectPosition: 'center',
    badge: 'Vedic A2 Ghee',
    title: 'Bilona Method',
    description: 'Nutrient Rich • Desi Cow',
    ctaText: 'Shop Ghee',
  },
  {
    id: 'dryfruits',
    href: '/search?q=dry%20fruits',
    themeClass: 'theme-dryfruits',
    imageUrl:
      'https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&w=600&q=85',
    imageAlt: 'Dry Fruits',
    objectPosition: 'center',
    badge: 'Premium Crunch',
    title: 'Organic Nuts',
    description: 'Jumbo Size • Handpicked',
    ctaText: 'Buy Now',
  },
];

export const DEFAULT_ENERGY_CARDS: EnergyCardData[] = [
  {
    id: 'marketplace',
    title: 'Multi-vendor marketplace',
    body: 'Thousands of sellers, one trusted checkout.',
    icon: 'fa-shop',
    badge: 'Scale Strength',
  },
  {
    id: 'b2b',
    title: 'B2B wholesale',
    body: 'Tiered pricing, GST invoicing, bulk orders.',
    icon: 'fa-briefcase',
    badge: 'Enterprise Ready',
  },
  {
    id: 'gst',
    title: 'GST-native billing',
    body: 'CGST/SGST/IGST handled automatically on every order.',
    icon: 'fa-file-invoice-dollar',
    badge: 'GST Native',
  },
];

export const DEFAULT_SECTION_HEADERS: Required<SectionHeadersData> = {
  popularPicks: {
    badge: '🔥 Trending Now',
    title: 'Popular Picks',
    subtitle: 'Handpicked premium selections curated by our top shopping experts',
  },
  bottomGrid2: {
    badge: '✨ Fresh Picks',
    title: 'More to Explore',
    subtitle: 'Discover unique products tailored for your lifestyle and home',
  },
  bestSelling: {
    title: 'Best Selling',
    badge: '🏆 Customer Favourites',
    subtitle: 'Highly rated items that our shoppers love this week',
    viewAllLabel: 'View all →',
  },
  topSellers: {
    title: 'Top Sellers',
    subtitle: 'Trusted stores from our marketplace',
    viewAllLabel: 'View all →',
  },
  flashSale: {
    badge: '⚡ LIMITED FLASH OFFER',
    heading: 'Gharghuti Spices & Sweets Flash Sale',
    subheading: 'Limited time offer! Grab the best deals before time runs out.',
  },
};

/** Merge admin override onto defaults so any missing key falls back gracefully. */
export function mergeHeaders(override?: SectionHeadersData | null): Required<SectionHeadersData> {
  if (!override) return DEFAULT_SECTION_HEADERS;
  return {
    popularPicks: { ...DEFAULT_SECTION_HEADERS.popularPicks, ...(override.popularPicks ?? {}) },
    bottomGrid2: { ...DEFAULT_SECTION_HEADERS.bottomGrid2, ...(override.bottomGrid2 ?? {}) },
    bestSelling: { ...DEFAULT_SECTION_HEADERS.bestSelling, ...(override.bestSelling ?? {}) },
    topSellers: { ...DEFAULT_SECTION_HEADERS.topSellers, ...(override.topSellers ?? {}) },
    flashSale: { ...DEFAULT_SECTION_HEADERS.flashSale, ...(override.flashSale ?? {}) },
  };
}
